import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  Schedule,
  Add,
  Delete,
  CheckCircle,
  AccessTime,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import axiosInstance from '../services/axios';

interface TimeWindow {
  dayOfWeek: string;
  startTime: string;
  endTime: string;
}

interface CreateSlotsRequest {
  startDate: string;
  endDate: string;
  timeWindows: TimeWindow[];
  timezone: string;
}

const ProviderAvailabilityPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  const [availabilityData, setAvailabilityData] = useState<{
    startDate: string;
    endDate: string;
    timezone: string;
  }>({
    startDate: new Date().toISOString().split('T')[0], // Today
    endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
    timezone: 'America/New_York',
  });

  const [timeWindows, setTimeWindows] = useState<TimeWindow[]>([
    {
      dayOfWeek: 'Monday',
      startTime: '09:00',
      endTime: '17:00',
    },
  ]);

  const daysOfWeek = [
    'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
  ];

  const timezones = [
    'America/New_York',
    'America/Chicago', 
    'America/Denver',
    'America/Los_Angeles',
    'America/Phoenix',
    'America/Anchorage',
    'Pacific/Honolulu',
  ];

  // Check if user is a provider
  if (user?.userType !== 'provider') {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <Alert severity="error">
          Only providers can access this page.
          <Button onClick={() => navigate('/')} sx={{ ml: 2 }}>
            Go Home
          </Button>
        </Alert>
      </Container>
    );
  }

  const handleAddTimeWindow = () => {
    setTimeWindows(prev => [...prev, {
      dayOfWeek: 'Monday',
      startTime: '09:00',
      endTime: '17:00',
    }]);
  };

  const handleRemoveTimeWindow = (index: number) => {
    setTimeWindows(prev => prev.filter((_, i) => i !== index));
  };

  const handleTimeWindowChange = (index: number, field: keyof TimeWindow, value: string) => {
    setTimeWindows(prev => prev.map((window, i) => 
      i === index ? { ...window, [field]: value } : window
    ));
  };

  const calculateTotalSlots = (): number => {
    const startDate = new Date(availabilityData.startDate);
    const endDate = new Date(availabilityData.endDate);
    const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    
    let totalSlots = 0;
    
    for (let day = 0; day <= daysDiff; day++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + day);
      const dayName = currentDate.toLocaleDateString('en-US', { weekday: 'long' });
      
      const dayWindows = timeWindows.filter(w => w.dayOfWeek === dayName);
      
      for (const window of dayWindows) {
        const startTime = new Date(`2024-01-01T${window.startTime}:00`);
        const endTime = new Date(`2024-01-01T${window.endTime}:00`);
        const durationMs = endTime.getTime() - startTime.getTime();
        const slots = Math.floor(durationMs / (15 * 60 * 1000)); // 15-minute slots
        totalSlots += slots;
      }
    }
    
    return totalSlots;
  };

  const handleCreateSlots = async () => {
    try {
      setLoading(true);
      setError(null);

      const request: CreateSlotsRequest = {
        startDate: availabilityData.startDate,
        endDate: availabilityData.endDate,
        timeWindows: timeWindows,
        timezone: availabilityData.timezone,
      };

      const response = await axiosInstance.post('/provider/create-slots', request);
      
      if (response.data) {
        setSuccess(true);
        
        // Redirect to dashboard after success
        setTimeout(() => {
          navigate('/provider/dashboard');
        }, 3000);
      } else {
        setError('Failed to create availability slots');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create availability slots. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <Card sx={{ py: 6 }}>
          <CardContent>
            <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              Availability Created!
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Your appointment slots have been created and are now available for patient booking.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You'll be redirected to your dashboard shortly.
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Button 
          startIcon={<ArrowBack />} 
          onClick={() => navigate('/provider/dashboard')}
          sx={{ mr: 2 }}
        >
          Back to Dashboard
        </Button>
        <div>
          <Typography variant="h3" component="h1">
            Set Availability
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Create appointment slots for patient booking (15-minute intervals)
          </Typography>
        </div>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Date Range & Settings */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Date Range & Settings
              </Typography>
              
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                value={availabilityData.startDate}
                onChange={(e) => setAvailabilityData(prev => ({
                  ...prev,
                  startDate: e.target.value
                }))}
                sx={{ mb: 2 }}
                InputLabelProps={{ shrink: true }}
              />
              
              <TextField
                label="End Date"
                type="date"
                fullWidth
                value={availabilityData.endDate}
                onChange={(e) => setAvailabilityData(prev => ({
                  ...prev,
                  endDate: e.target.value
                }))}
                sx={{ mb: 2 }}
                InputLabelProps={{ shrink: true }}
              />
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Timezone</InputLabel>
                <Select
                  value={availabilityData.timezone}
                  onChange={(e) => setAvailabilityData(prev => ({
                    ...prev,
                    timezone: e.target.value
                  }))}
                  label="Timezone"
                >
                  {timezones.map((tz) => (
                    <MenuItem key={tz} value={tz}>
                      {tz.replace('_', ' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Paper sx={{ p: 2, backgroundColor: 'primary.light', color: 'primary.contrastText' }}>
                <Typography variant="subtitle1" gutterBottom>
                  <AccessTime sx={{ fontSize: 20, mr: 1, verticalAlign: 'middle' }} />
                  Total Slots to Create
                </Typography>
                <Typography variant="h4">
                  {calculateTotalSlots()}
                </Typography>
                <Typography variant="caption">
                  15-minute appointment slots
                </Typography>
              </Paper>
            </CardContent>
          </Card>
        </Grid>

        {/* Time Windows */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Weekly Schedule
                </Typography>
                <Button 
                  startIcon={<Add />}
                  onClick={handleAddTimeWindow}
                  variant="outlined"
                  size="small"
                >
                  Add Time Window
                </Button>
              </Box>

              <List>
                {timeWindows.map((window, index) => (
                  <React.Fragment key={index}>
                    <ListItem sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Day</InputLabel>
                            <Select
                              value={window.dayOfWeek}
                              onChange={(e) => handleTimeWindowChange(index, 'dayOfWeek', e.target.value)}
                              label="Day"
                            >
                              {daysOfWeek.map((day) => (
                                <MenuItem key={day} value={day}>
                                  {day}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            label="Start Time"
                            type="time"
                            fullWidth
                            size="small"
                            value={window.startTime}
                            onChange={(e) => handleTimeWindowChange(index, 'startTime', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            label="End Time"
                            type="time"
                            fullWidth
                            size="small"
                            value={window.endTime}
                            onChange={(e) => handleTimeWindowChange(index, 'endTime', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Button
                            startIcon={<Delete />}
                            onClick={() => handleRemoveTimeWindow(index)}
                            color="error"
                            variant="outlined"
                            size="small"
                            fullWidth
                            disabled={timeWindows.length === 1}
                          >
                            Remove
                          </Button>
                        </Grid>
                      </Grid>
                    </ListItem>
                    {index < timeWindows.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>

              <Alert severity="info" sx={{ mt: 2 }}>
                Each time window will generate 15-minute appointment slots that patients can book.
                Make sure your schedule allows for adequate time between patients.
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Create Button */}
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={loading ? <CircularProgress size={20} /> : <Schedule />}
          onClick={handleCreateSlots}
          disabled={loading || timeWindows.length === 0}
          sx={{ minWidth: 200, py: 1.5 }}
        >
          {loading ? 'Creating...' : `Create ${calculateTotalSlots()} appointment slots`}
        </Button>
      </Box>
    </Container>
  );
};

export default ProviderAvailabilityPage;